import React, { forwardRef } from 'react';
import './QuotePreview.css';

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === '' || amount === null || isNaN(amount)) {
    return 'TBD';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * QuotePreview component for displaying quote details
 * 
 * This is a forwardRef component so the parent can capture it for PDF/image generation
 */
const QuotePreview = forwardRef(({ quoteDetails, projectName, style = 'commercial', onTogglePresentation, showPresentation, isPageMode }, ref) => {
  const {
    sections,
    hardware,
    software,
    installation,
    deployment,
    support,
    totalOneTime,
    totalRecurring,
    discount,
    grandTotalOneTime,
    grandTotalRecurring,
    totalSensors
  } = quoteDetails;

  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Create hardware section items
  const hardwareItems = hardware.items.filter(item => item.quantity > 0);
  const hardwareTotalQty = hardwareItems.reduce((sum, item) => sum + item.quantity, 0);

  // Create software section items
  const softwareItems = software.items.filter(item => item.quantity > 0);
  const softwareTotalQty = softwareItems.reduce((sum, item) => sum + item.quantity, 0);

  // Find specific items within the support section
  const entryItems = support.items.filter(item => 
    item.name === 'Entry Sensor'
  );
  const oaItems = support.items.filter(item => 
    item.name === 'Open Area Sensor'
  );
  const waffleItems = support.items.filter(item => 
    item.name.includes('Waffle')
  );

  // Calculate Entry + OA setup fee
  const entryOAQuantity = entryItems.reduce((sum, item) => sum + item.quantity, 0) +
                          oaItems.reduce((sum, item) => sum + item.quantity, 0);
  const entryOAUnitPrice = entryItems.length > 0 ? entryItems[0].unitPrice : (oaItems.length > 0 ? oaItems[0].unitPrice : 0);
  const entryOATotal = entryOAQuantity * entryOAUnitPrice;

  // Calculate combined Waffle setup fee
  const waffleQuantity = waffleItems.reduce((sum, item) => sum + item.quantity, 0);
  const waffleUnitPrice = waffleItems.length > 0 ? waffleItems[0].unitPrice : 0;
  const waffleTotal = waffleQuantity * waffleUnitPrice;

  // Create services items (combine installation, deployment, support)
  const serviceItems = [
    // Only include Entry/OA setup if there are any
    ...(entryOAQuantity > 0 ? [{
      name: "OA, Entry Setup (one-time)",
      quantity: entryOAQuantity,
      unitPrice: entryOAUnitPrice,
      total: entryOATotal,
      category: 'support'
    }] : []),
    
    // Include a single Waffle setup fee if there are any waffles
    ...(waffleQuantity > 0 ? [{
      name: "Waffle Setup (one-time)",
      quantity: waffleQuantity,
      unitPrice: waffleUnitPrice,
      total: waffleTotal,
      category: 'support'
    }] : []),
    
    {
      name: "Premium Support (annual)",
      quantity: 1,
      unitPrice: "10% of software",
      total: software.total * 0.1,
      category: 'support'
    }
  ];

  // Calculate the total for all service items
  const servicesTotalQuantity = serviceItems.reduce((sum, item) => 
    sum + (typeof item.quantity === 'number' ? item.quantity : 0), 0);
  const servicesTotalAmount = serviceItems.reduce((sum, item) => 
    sum + (typeof item.total === 'number' ? item.total : 0), 0);
    
  // Calculate one-time and recurring costs from services
  const oneTimeServiceItems = serviceItems.filter(item => !item.name.includes('annual'));
  const recurringServiceItems = serviceItems.filter(item => item.name.includes('annual'));
  
  const oneTimeServicesTotal = oneTimeServiceItems.reduce((sum, item) => 
    sum + (typeof item.total === 'number' ? item.total : 0), 0);
  const recurringServicesTotal = recurringServiceItems.reduce((sum, item) => 
    sum + (typeof item.total === 'number' ? item.total : 0), 0);
  
  // Calculate corrected grand totals
  const correctedGrandTotalOneTime = hardware.total + oneTimeServicesTotal;
  const correctedGrandTotalRecurring = software.total + recurringServicesTotal;
  
  // Debug logging
  console.log('Cost breakdown:', {
    hardwareTotal: hardware.total,
    softwareTotal: software.total,
    oneTimeServicesTotal,
    recurringServicesTotal,
    correctedGrandTotalOneTime,
    correctedGrandTotalRecurring,
    serviceItems,
    oneTimeServiceItems,
    recurringServiceItems
  });

  // Helper function to render a section table (for original style)
  const renderSection = (title, data) => {
    if (!data.items.length) return null;

    return (
      <div className="quote-section">
        <h3>{title}</h3>
        <table className="quote-table">
          <thead>
            <tr>
              <th>Item(s)</th>
              <th>Quantity</th>
              <th>Per Unit</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.items
              .filter(item => item.quantity > 0)
              .map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.unitPrice)}</td>
                  <td>{formatCurrency(item.total)}</td>
                </tr>
              ))}
            <tr className="section-total">
              <td colSpan="3">Total</td>
              <td>{formatCurrency(data.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render the original style quote
  if (style === 'original') {
    return (
      <div className="quote-preview" ref={ref}>
        <div className="quote-header-banner">
          <div className="company-info">
            <h1>Romulus</h1>
            <p>Workspace Occupancy Solutions</p>
          </div>
          <div className="quote-info">
            <h2>Project Quote</h2>
            <p>Date: {formattedDate}</p>
            <p>Project: {projectName}</p>
            <p>Total Sensors: {totalSensors}</p>
          </div>
        </div>

        {/* Hardware Section */}
        {sections.hardware && renderSection('1. Hardware Costs', hardware)}

        {/* Software Section */}
        {sections.software && renderSection('2. Software/Platform Costs (annual)', software)}

        {/* Installation Section */}
        {sections.installation && renderSection('3. Installation Costs', installation)}

        {/* Deployment Section */}
        {sections.deployment && renderSection('4. Deployment Costs', deployment)}

        {/* Support Section */}
        {sections.support && renderSection('5. Warranty and Support Costs', support)}

        {/* Quote Summary */}
        <div className="quote-summary">
          <div className="summary-box">
            <h3>YEAR 1</h3>
            <p className="summary-item">
              <span>Total One-time Cost:</span>
              <span>{formatCurrency(correctedGrandTotalOneTime)}</span>
            </p>
            <p className="summary-item">
              <span>Total Recurring Cost:</span>
              <span>{formatCurrency(correctedGrandTotalRecurring)}</span>
            </p>
            {/* Discount section commented out */}
            {/*
            {discount > 0 && (
              <p className="summary-item discount">
                <span>Discount Applied:</span>
                <span>-{formatCurrency(discount)}</span>
              </p>
            )}
            */}
          </div>

          <div className="summary-box">
            <h3>YEAR 2</h3>
            <p className="summary-item">
              <span>Total Recurring Cost:</span>
              <span>{formatCurrency(correctedGrandTotalRecurring)}</span>
            </p>
          </div>
        </div>

        <div className="quote-footer">
          <p>5-Year Warranty Included</p>
        </div>
      </div>
    );
  }

  // Render the commercial style quote (default)
  return (
    <div className="commercial-quote">
      <div className="commercial-content">
        <div className="commercial-title">
          <h2>{projectName || 'Project'}</h2>
          <p>HARDWARE, SOFTWARE & SERVICES OVER A 1 YEAR TERM</p>
        </div>

        <div className="commercial-tables">
          <div className="commercial-tables-left">
            {/* Hardware Table */}
            <div className="commercial-section">
              <h3 className="commercial-section-title">HARDWARE</h3>
              <table className="commercial-table">
                <thead>
                  <tr>
                    <th>Item(s)</th>
                    <th>Quantity</th>
                    <th>Per Unit (one-time)</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {hardwareItems.map((item, index) => (
                    <tr key={`hw-${index}`}>
                      <td>{item.name.replace(' Sensor', '')}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unitPrice)}</td>
                      <td>{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td>Total</td>
                    <td>{hardwareTotalQty}</td>
                    <td>n/a</td>
                    <td>{formatCurrency(hardware.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Software Table */}
            <div className="commercial-section">
              <h3 className="commercial-section-title">SOFTWARE (annual)</h3>
              <table className="commercial-table">
                <thead>
                  <tr>
                    <th>Item(s)</th>
                    <th>Quantity</th>
                    <th>Per Unit (annual)</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {softwareItems.map((item, index) => (
                    <tr key={`sw-${index}`}>
                      <td>{item.name.replace(' Sensor', '')}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unitPrice)}</td>
                      <td>{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td>Total</td>
                    <td>{softwareTotalQty}</td>
                    <td>n/a</td>
                    <td>{formatCurrency(software.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="commercial-tables-right">
            {/* Services Table */}
            <div className="commercial-section">
              <h3 className="commercial-section-title">SERVICES</h3>
              <table className="commercial-table">
                <thead>
                  <tr>
                    <th>Item(s)</th>
                    <th>Quantity</th>
                    <th>Per Unit</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceItems.map((item, index) => (
                    <tr key={`service-${index}`}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.unitPrice === null ? 'TBD*' : (typeof item.unitPrice === 'string' ? item.unitPrice : formatCurrency(item.unitPrice))}</td>
                      <td>{item.total === 0 && item.category === 'deployment' ? 'TBD' : formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td>Total</td>
                    <td>{servicesTotalQuantity}</td>
                    <td>n/a</td>
                    <td>{formatCurrency(servicesTotalAmount)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Year Summary Box */}
            <div className="year-summary-box">
              <div className="year-container">
                <h3>YEAR 1</h3>
                <p>
                  <span>Total One-time Cost:</span>
                  <span>{formatCurrency(correctedGrandTotalOneTime)}</span>
                </p>
                <p>
                  <span>Total Recurring Cost:</span>
                  <span>{formatCurrency(correctedGrandTotalRecurring)}</span>
                </p>
              </div>
              
              <div className="year-container">
                <h3>YEAR 2</h3>
                <p>
                  <span>Total Recurring Cost:</span>
                  <span>{formatCurrency(correctedGrandTotalRecurring)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="commercial-footer">
          <div className="page-number">
            <span>8</span>
            <span className="footer-divider">|</span>
            {/* Removing "DENSITY" text as requested */}
            {/* <span>DENSITY</span> */}
          </div>
        </div>

        {/* Horizontal line divider for presentation content */}
        <hr className="cost-summary-divider" />
        
        {/* Presentation toggle button - moved here */}
        {onTogglePresentation && !isPageMode && (
          <div className="presentation-toggle">
            <button 
              className="toggle-presentation-btn"
              onClick={onTogglePresentation}
            >
              {/* This toggles the Presentation component in the QuoteGenerator parent */}
              {showPresentation ? "Hide Presentation" : "Show Presentation"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

QuotePreview.displayName = 'QuotePreview';

export default QuotePreview; 